import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Save, CheckCircle2 } from "lucide-react";
import { updateOrder } from "@/lib/api";
import { MenuStep } from "@/components/MenuStep";

const LOCATIONS = ["Aylesbury", "Whitehouse", "Bletchley", "Glebefarm", "Hatfield", "Uxbridge"];

// requested_at from backend looks like "2026-02-22T14:47:00" — split it.
const splitRequestedAt = (iso) => {
  if (!iso || typeof iso !== "string") return { date: "", time: "" };
  const [d, t = ""] = iso.split("T");
  return { date: d || "", time: (t || "").slice(0, 5) };
};

const Field = ({ label, required, children, testId, hint }) => (
  <div className="flex flex-col gap-1.5" data-testid={testId}>
    <label className="label-uppercase flex items-center gap-1">
      {required && <span className="text-dotr-primary">*</span>}
      {label}
    </label>
    {children}
    {hint && <span className="font-mono text-[11px] text-dotr-inkmuted">{hint}</span>}
  </div>
);

const MealCard = ({ label, time, checked, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    aria-pressed={checked}
    className={`flex-1 text-left p-4 border-2 border-dotr-ink transition-all ${
      checked
        ? "bg-dotr-ink text-white shadow-brutalSm translate-x-[2px] translate-y-[2px]"
        : "bg-white text-dotr-ink shadow-brutal hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-brutalSm"
    }`}
  >
    <div className="flex items-center justify-between">
      <div>
        <div className="font-heading font-semibold uppercase tracking-wider text-lg">{label}</div>
        <div className={`font-mono text-xs ${checked ? "text-white/70" : "text-dotr-inkmuted"}`}>
          Run @ {time}
        </div>
      </div>
      <div className={`w-6 h-6 border-2 ${checked ? "border-white bg-white" : "border-dotr-ink bg-white"} flex items-center justify-center`}>
        {checked && <CheckCircle2 size={14} className="text-dotr-ink" />}
      </div>
    </div>
  </button>
);

export const EditOrderModal = ({ order, onClose, onSaved }) => {
  const { date: initDate, time: initTime } = splitRequestedAt(order?.requested_at);
  const [details, setDetails] = useState({
    customer_name: order?.customer_name || "",
    email: order?.email || "",
    phone: order?.phone || "",
    pickup_delivery: order?.pickup_delivery || "",
    spoke_location: order?.spoke_location || "",
    lunch: !!order?.lunch,
    dinner: !!order?.dinner,
    requested_date: initDate,
    requested_time: initTime,
    special_instructions: order?.special_instructions || "",
  });
  const [items, setItems] = useState(() => {
    const m = {};
    (order?.items || []).forEach((it) => {
      if (it?.name) m[it.name] = it.quantity || 1;
    });
    return m;
  });
  const [menuPrices, setMenuPrices] = useState({});
  const [saving, setSaving] = useState(false);

  const totalQty = useMemo(
    () => Object.values(items).reduce((s, n) => s + (parseInt(n, 10) || 0), 0),
    [items]
  );
  const grandTotal = useMemo(
    () =>
      Object.entries(items).reduce(
        (sum, [name, qty]) => sum + (menuPrices[name] || 0) * (parseInt(qty, 10) || 0),
        0
      ),
    [items, menuPrices]
  );

  const update = (key) => (e) => {
    const val = e?.target ? e.target.value : e;
    setDetails((f) => ({ ...f, [key]: val }));
  };

  const submit = async () => {
    if (!details.customer_name.trim()) return toast.error("Customer name is required");
    if (!details.pickup_delivery) return toast.error("Select Pickup or Delivery");
    if (!details.spoke_location) return toast.error("Select a Spoke Location");
    if (!details.lunch && !details.dinner) return toast.error("Pick Lunch or Dinner");
    if (!details.requested_date || !details.requested_time) return toast.error("Requested date & time required");
    if (Object.keys(items).length === 0) return toast.error("Add at least one menu item");

    const itemList = Object.entries(items).map(([name, quantity]) => ({
      category: "",
      name,
      quantity: parseInt(quantity, 10) || 1,
    }));

    setSaving(true);
    try {
      const requested_at = `${details.requested_date}T${details.requested_time}:00`;
      const payload = {
        customer_name: details.customer_name.trim(),
        email: details.email.trim() || null,
        phone: details.phone.trim() || null,
        pickup_delivery: details.pickup_delivery,
        spoke_location: details.spoke_location,
        lunch: details.lunch,
        dinner: details.dinner,
        requested_at,
        special_instructions: details.special_instructions.trim() || null,
        items: itemList,
      };
      await updateOrder(order.id, payload);
      toast.success("Order updated.", {
        description: `${details.customer_name} · ${totalQty} item${totalQty === 1 ? "" : "s"} · £${grandTotal.toFixed(2)}`,
      });
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-3 md:p-6 bg-black/60 overflow-y-auto"
      onClick={onClose}
      data-testid="edit-order-modal"
    >
      <div
        className="brutal-card w-full max-w-5xl bg-dotr-bg my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-dotr-ink p-4 md:p-5 sticky top-0 bg-dotr-bg z-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted">Admin · Edit Order</div>
            <div className="font-heading font-semibold text-xl md:text-2xl uppercase tracking-tight">
              #{(order.id || "").slice(0, 8)} · {order.customer_name}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="brutal-btn-ghost w-10 h-10 flex items-center justify-center"
            aria-label="Close"
            data-testid="btn-close-edit-modal"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 md:p-7 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <Field label="Customer Name" required testId="edit-field-customer-name">
              <input
                data-testid="edit-input-customer-name"
                className="brutal-input h-11 px-3 font-body text-base w-full"
                value={details.customer_name}
                onChange={update("customer_name")}
                maxLength={120}
              />
            </Field>
            <Field label="Phone" testId="edit-field-phone">
              <input
                data-testid="edit-input-phone"
                className="brutal-input h-11 px-3 font-body text-base w-full"
                value={details.phone}
                onChange={update("phone")}
                inputMode="tel"
              />
            </Field>
            <Field label="Email" testId="edit-field-email">
              <input
                data-testid="edit-input-email"
                type="email"
                className="brutal-input h-11 px-3 font-body text-base w-full"
                value={details.email}
                onChange={update("email")}
              />
            </Field>
            <Field label="Pickup / Delivery" required testId="edit-field-pickup-delivery">
              <select
                data-testid="edit-select-pickup-delivery"
                className="brutal-input h-11 px-3 font-body text-base w-full bg-white"
                value={details.pickup_delivery}
                onChange={update("pickup_delivery")}
              >
                <option value="">— Select —</option>
                <option value="Pickup">Pickup</option>
                <option value="Delivery">Delivery</option>
              </select>
            </Field>
            <Field label="Spoke Location" required testId="edit-field-spoke-location">
              <select
                data-testid="edit-select-spoke-location"
                className="brutal-input h-11 px-3 font-body text-base w-full bg-white"
                value={details.spoke_location}
                onChange={update("spoke_location")}
              >
                <option value="">— Select —</option>
                {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Requested Date" required testId="edit-field-requested-date">
              <input
                data-testid="edit-input-requested-date"
                type="date"
                className="brutal-input h-11 px-3 font-body text-base w-full"
                value={details.requested_date}
                onChange={update("requested_date")}
              />
            </Field>
            <Field label="Requested Time" required testId="edit-field-requested-time">
              <input
                data-testid="edit-input-requested-time"
                type="time"
                className="brutal-input h-11 px-3 font-body text-base w-full"
                value={details.requested_time}
                onChange={update("requested_time")}
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Meal Service" required testId="edit-field-meal">
                <div className="flex flex-col sm:flex-row gap-4 mt-1">
                  <MealCard
                    label="Lunch"
                    time="12:45 PM"
                    checked={details.lunch}
                    onChange={(v) => setDetails((f) => ({ ...f, lunch: v }))}
                    testId="edit-meal-lunch-card"
                  />
                  <MealCard
                    label="Dinner"
                    time="4:30 PM"
                    checked={details.dinner}
                    onChange={(v) => setDetails((f) => ({ ...f, dinner: v }))}
                    testId="edit-meal-dinner-card"
                  />
                </div>
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Special Instructions" testId="edit-field-special-instructions">
                <textarea
                  data-testid="edit-input-special-instructions"
                  className="brutal-input p-3 font-body text-base w-full min-h-[90px]"
                  rows={3}
                  value={details.special_instructions}
                  onChange={update("special_instructions")}
                />
              </Field>
            </div>
          </div>

          <div className="border-t-2 border-dotr-ink pt-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-3">
              Menu items
            </div>
            <MenuStep
              value={items}
              onChange={setItems}
              totalQty={totalQty}
              onPricesLoaded={setMenuPrices}
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-dotr-bg border-t-2 border-dotr-ink p-4 md:p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-col">
            <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
              {Object.keys(items).length} item types · {totalQty} qty
            </span>
            <span className="font-heading font-semibold text-2xl text-dotr-primary tracking-tighter" data-testid="edit-grand-total">
              £{grandTotal.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="brutal-btn-ghost px-5 py-3 text-sm"
              data-testid="btn-cancel-edit"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              data-testid="btn-save-edit"
              className="brutal-btn px-6 py-3 text-sm flex items-center gap-2 disabled:opacity-70"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;
